import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import BreadcrumbService from '../../services/breadcrumbService';

const Breadcrumbs = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [participantDetails, setParticipantDetails] = useState(null);
    const [processedCrumbs, setProcessedCrumbs] = useState([]);
    
    useEffect(() => {
        const loadBreadcrumbData = async () => {
            try {
                // Force refresh to get the latest participant data
                const data = await BreadcrumbService.getBreadcrumbData(true);
                buildBreadcrumbs(data);
            } catch (error) {
                console.error('Failed to load breadcrumb data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadBreadcrumbData();
    }, [location]);

    const segmentsToHide = [
      'participant',
      'participants',
      'studies'
    ];

    const segmentDict = {
      'studies': 'Studies',
      'studybuilder': 'Study Builder',
      'drafts': 'Study Drafts',
      'library': 'Library',
      'schedule': 'Schedule',
      'profile': 'Profile',
      'compliance': 'Compliance',
      'edit': 'Edit',
      'file-upload': 'File Upload',
      'sites': 'Sites',
      'participant': 'Participant',
      'add-participant': 'Add Participant',
      'dcq': 'DCQ',
      'create': 'Create',
      'create_dcq': 'Create DCQ',
      'add-site': 'Add Site',
      'upload': 'Upload',
      'global-library': 'Global Library'
    };

    // IDs in the address and in the lookup can differ in dashes and case
    // (SQLite returns UUIDs without dashes), so compare them in one form.
    const idKey = (value) => String(value || '').replace(/-/g, '').toLowerCase();

    const lookupName = (table, segment) => {
      if (!table) return null;
      const direct = table[segment];
      const match = direct !== undefined
        ? direct
        : Object.entries(table).find(([key]) => idKey(key) === idKey(segment))?.[1];
      if (match == null) return null;
      return typeof match === 'object' ? match.name || null : match;
    };

    const buildBreadcrumbs = (breadcrumbData) => {
      let pathName =  location.pathname;
      let pathnames = pathName.split('/').filter((x) => x);
      // Process the path segments
      let crumbs = [];
      let currentPath = '';
      const data = breadcrumbData || {};

      crumbs.push({
        name: 'Home',
        path: '/',
        isClickable: true
      });

      pathnames.forEach((segment, index) => {
        currentPath += '/' + segment;
        const prevName = index > 0 ? pathnames[index - 1] : '';

        if (segmentsToHide.includes(segment)) {
          // ignore segment
        }
        else if (segmentDict[segment] != null) {
          crumbs.push({
            name: segmentDict[segment],
            path: currentPath,
            isClickable: true
          });
        }
        else if (prevName === 'study' || prevName === 'studies') {
          crumbs.push({
            name: decodeSegment(lookupName(data.studies, segment) || 'Study'),
            path: currentPath,
            isClickable: true
          });
        }
        else if (prevName === 'site' || prevName === 'sites') {
          crumbs.push({
            name: decodeSegment(lookupName(data.sites, segment) || 'Site'),
            path: currentPath,
            isClickable: true
          });
        }
        else if (prevName === 'participant' || prevName === 'participants') {
          crumbs.push({
            name: decodeSegment(lookupName(data.participants, segment) || 'Participant'),
            path: currentPath,
            isClickable: true
          });
        }
        else if (prevName == 'completed-surveys') {

        }
        else {
          crumbs.push({
            name: decodeSegment(segment),
            path: currentPath,
            isClickable: true
          });
        }

        // show the 'tab' if the path ends with /sites or /dcq (the study tab id is "dcqs")
        let last = crumbs[crumbs.length - 1];
        last.path = last.path.replace(/\/sites$/, '#sites');
        last.path = last.path.replace(/\/dcq$/, '#dcqs');
      });

      setProcessedCrumbs(crumbs);
    };

    const decodeSegment = (segment) => {
      try {
          return decodeURIComponent(segment);
      } catch (error) {
          console.error('Error decoding segment:', segment, error);
          return segment;
      }
    };

    const handleClick = (e, segment) => {
        e.preventDefault();
        if (segment.path.includes('?tab=')) {
            const [path, query] = segment.path.split('?');
            navigate(path);
        } else {
            navigate(segment.path);
        }
    };

    return (
        <nav className="min-w-0 text-[13px]" aria-label="Breadcrumb">
            <ol className="flex min-w-0 items-center p-0">
                {processedCrumbs.map((segment, index) => {
                    const isLast = index === processedCrumbs.length - 1;
                    const isClickable = segment.isClickable && !isLast;
                    // Small screens show only the last two steps of the trail.
                    const hiddenOnPhone = index < processedCrumbs.length - 2;
                    const firstOnPhone = index === processedCrumbs.length - 2;

                    return (
                        <li key={segment.path} className={`${hiddenOnPhone ? 'hidden sm:flex' : 'flex'} items-center ${isLast ? 'min-w-0' : 'flex-shrink-0'}`}>
                            {index > 0 && (
                                <span className={`mx-1.5 text-gray-300 dark:text-gray-600 ${firstOnPhone ? 'hidden sm:inline' : ''}`} aria-hidden="true">/</span>
                            )}
                            {isClickable ? (
                                <Link
                                    to={segment.path}
                                    onClick={(e) => handleClick(e, segment)}
                                    className="max-w-[12rem] truncate rounded text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                                >
                                    {segment.name}
                                </Link>
                            ) : (
                                <span className="truncate font-medium text-gray-900 dark:text-gray-100" aria-current={isLast ? "page" : undefined}>
                                    {segment.name}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};

export default Breadcrumbs;